import { differenceInCalendarDays, isValid, format, addDays } from 'date-fns';
import { createDateSafe } from './dates';
import { isBlockedHolidayStart } from './vacationHolidayRules';

export const BLOCKED_START_WEEKDAYS: readonly number[] = [0, 4, 5, 6];

export const BLOCKED_WEEKDAY_MESSAGE =
    'As férias não podem começar em quinta, sexta, sábado ou domingo — apenas segunda, terça ou quarta-feira são permitidos como início.';

export const MIN_ADVANCE_DAYS = 40;

export const MIN_CORRECTION_LEAD_DAYS = 31;

export const MIN_ADVANCE_MESSAGE = `As férias só podem começar a partir de ${MIN_ADVANCE_DAYS} dias contados de hoje.`;

export function isBlockedStartWeekdayDate(date: Date): boolean {
    if (!isValid(date)) return false;
    return BLOCKED_START_WEEKDAYS.includes(date.getDay());
}

export function isBlockedStartWeekday(isoDate: string): boolean {
    const date = createDateSafe(isoDate);
    if (!date) return false;
    return isBlockedStartWeekdayDate(date);
}

export function isBeforeMinAdvanceDate(isoDate: string, today: Date = new Date()): boolean {
    const date = createDateSafe(isoDate);
    if (!date || !isValid(date)) return false;
    return differenceInCalendarDays(date, today) < MIN_ADVANCE_DAYS;
}

export function isBeforeEarliestStartDate(
    isoDate: string,
    earliestStartDate?: string | null
): boolean {
    if (!earliestStartDate) return false;
    const date = createDateSafe(isoDate);
    const earliest = createDateSafe(earliestStartDate);
    if (!date || !earliest || !isValid(date)) return false;
    return differenceInCalendarDays(date, earliest) < 0;
}

export function buildEarliestStartMessage(earliestStartDate: string): string {
    const date = createDateSafe(earliestStartDate);
    const formatted = date ? format(date, 'dd/MM/yyyy') : earliestStartDate;
    return `Suas férias só podem começar a partir de ${formatted}, quando você completa 12 meses de casa.`;
}

export function isAfterScheduleLimit(isoDate: string, scheduleLimitDate?: string | null): boolean {
    if (!scheduleLimitDate) return false;
    const date = createDateSafe(isoDate);
    const limit = createDateSafe(scheduleLimitDate);
    if (!date || !limit || !isValid(date)) return false;
    return differenceInCalendarDays(date, limit) > 0;
}

export function buildScheduleLimitMessage(scheduleLimitDate: string): string {
    const date = createDateSafe(scheduleLimitDate);
    const formatted = date ? format(date, 'dd/MM/yyyy') : scheduleLimitDate;
    return `Suas férias precisam começar até ${formatted} para caber no prazo.`;
}

export function isSelectableStartDate(
    isoDate: string,
    today: Date = new Date(),
    earliestStartDate?: string | null,
    holidays: string[] = [],
    scheduleLimitDate?: string | null
): boolean {
    const date = createDateSafe(isoDate);
    if (!date || !isValid(date)) return false;
    return (
        !isBlockedStartWeekdayDate(date) &&
        !isBeforeMinAdvanceDate(isoDate, today) &&
        !isBeforeEarliestStartDate(isoDate, earliestStartDate) &&
        !isBlockedHolidayStart(isoDate, holidays) &&
        !isAfterScheduleLimit(isoDate, scheduleLimitDate)
    );
}

export function findFirstSelectableStartDate(
    today: Date = new Date(),
    earliestStartDate?: string | null,
    holidays: string[] = [],
    scheduleLimitDate?: string | null
): string {
    const earliest = earliestStartDate ? createDateSafe(earliestStartDate) : null;
    let current = new Date(today);
    current.setHours(0, 0, 0, 0);
    if (earliest && earliest > current) {
        current = earliest;
    }

    for (let i = 0; i < 90; i++) {
        const isoDate = format(current, 'yyyy-MM-dd');
        if (isSelectableStartDate(isoDate, today, earliestStartDate, holidays, scheduleLimitDate)) {
            return isoDate;
        }
        current = addDays(current, 1);
    }

    return format(addDays(today, MIN_ADVANCE_DAYS), 'yyyy-MM-dd');
}
